import { TenantInfo } from '../types';

/** Keys de sesión viva (por pestaña). */
const SESSION_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  TENANT_ID: 'tenantId',
  TENANT_NAME: 'tenantName',
  TENANT_CODE: 'tenantCode',
  ROLES: 'roles',
  USERNAME: 'username',
  DISPLAY_NAME: 'displayName',
} as const;

/** Last-used en localStorage para seed de pestañas nuevas vacías. */
const LAST_USED_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  TENANT_ID: 'tenantId',
  TENANT_NAME: 'tenantName',
  TENANT_CODE: 'tenantCode',
  ROLES: 'roles',
  USERNAME: 'username',
  USER_NAME: 'userName',
  USER: 'user',
  DISPLAY_NAME: 'displayName',
  TENANTS_LIST: 'tenants',
} as const;

export const TAB_TENANT_QUERY = 'tabTenant';
export const AUTH_BROADCAST_CHANNEL = 'eone-auth';

export type AuthBroadcastMessage =
  | {
      type: 'token-refresh';
      tenantId: number | null;
      token: string;
      refreshToken: string;
      roles?: string[];
      username?: string;
      displayName?: string;
      tenantName?: string | null;
      tenantCode?: string | null;
    }
  | { type: 'logout-all' };

export interface TabSessionSnapshot {
  token: string | null;
  refreshToken: string | null;
  tenantId: number | null;
  tenantName: string | null;
  tenantCode: string | null;
  roles: string[];
  username: string | null;
  displayName: string | null;
}

const safeGet = (store: Storage, key: string): string | null => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (store: Storage, key: string, value: string): void => {
  try {
    store.setItem(key, value);
  } catch {
    // ignore quota / private mode
  }
};

const safeRemove = (store: Storage, key: string): void => {
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
};

export const getAccessToken = (): string | null => safeGet(sessionStorage, SESSION_KEYS.TOKEN);

export const getRefreshToken = (): string | null =>
  safeGet(sessionStorage, SESSION_KEYS.REFRESH_TOKEN);

export const getSessionTenantId = (): number | null => {
  const v = safeGet(sessionStorage, SESSION_KEYS.TENANT_ID);
  if (!v) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const readTabSession = (): TabSessionSnapshot => {
  let roles: string[] = [];
  try {
    const raw = safeGet(sessionStorage, SESSION_KEYS.ROLES);
    roles = raw ? JSON.parse(raw) : [];
  } catch {
    roles = [];
  }
  const tid = safeGet(sessionStorage, SESSION_KEYS.TENANT_ID);
  return {
    token: safeGet(sessionStorage, SESSION_KEYS.TOKEN),
    refreshToken: safeGet(sessionStorage, SESSION_KEYS.REFRESH_TOKEN),
    tenantId: tid ? parseInt(tid, 10) : null,
    tenantName: safeGet(sessionStorage, SESSION_KEYS.TENANT_NAME),
    tenantCode: safeGet(sessionStorage, SESSION_KEYS.TENANT_CODE),
    roles,
    username: safeGet(sessionStorage, SESSION_KEYS.USERNAME),
    displayName: safeGet(sessionStorage, SESSION_KEYS.DISPLAY_NAME),
  };
};

export const getTenantsList = (): TenantInfo[] => {
  try {
    const raw = safeGet(localStorage, LAST_USED_KEYS.TENANTS_LIST);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const setTenantsList = (tenants: TenantInfo[]): void => {
  if (tenants.length) {
    safeSet(localStorage, LAST_USED_KEYS.TENANTS_LIST, JSON.stringify(tenants));
  } else {
    safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
  }
};

export interface PersistSessionInput {
  token: string;
  refreshToken: string;
  tenantId?: number | null;
  tenantName?: string | null;
  tenantCode?: string | null;
  roles?: string[];
  username?: string | null;
  displayName?: string | null;
  /** Actualiza last-used en localStorage (default true). */
  updateLastUsed?: boolean;
}

/** Escribe la sesión viva de esta pestaña y opcionalmente el last-used global. */
export const persistTabSession = (data: PersistSessionInput): void => {
  safeSet(sessionStorage, SESSION_KEYS.TOKEN, data.token);
  safeSet(sessionStorage, SESSION_KEYS.REFRESH_TOKEN, data.refreshToken);
  if (data.roles) {
    safeSet(sessionStorage, SESSION_KEYS.ROLES, JSON.stringify(data.roles));
  }
  if (data.username) {
    safeSet(sessionStorage, SESSION_KEYS.USERNAME, data.username);
  }
  if (data.displayName) {
    safeSet(sessionStorage, SESSION_KEYS.DISPLAY_NAME, data.displayName);
  }
  if (data.tenantId != null) {
    safeSet(sessionStorage, SESSION_KEYS.TENANT_ID, String(data.tenantId));
    safeSet(sessionStorage, SESSION_KEYS.TENANT_NAME, data.tenantName ?? '');
    safeSet(sessionStorage, SESSION_KEYS.TENANT_CODE, data.tenantCode ?? '');
  } else if (data.tenantId === null) {
    safeRemove(sessionStorage, SESSION_KEYS.TENANT_ID);
    safeRemove(sessionStorage, SESSION_KEYS.TENANT_NAME);
    safeRemove(sessionStorage, SESSION_KEYS.TENANT_CODE);
  }

  if (data.updateLastUsed !== false) {
    writeLastUsedFromSession();
  }
};

/** Copia sessionStorage → localStorage (seed para pestañas nuevas). */
export const writeLastUsedFromSession = (): void => {
  const snap = readTabSession();
  if (!snap.token) return;
  safeSet(localStorage, LAST_USED_KEYS.TOKEN, snap.token);
  if (snap.refreshToken) {
    safeSet(localStorage, LAST_USED_KEYS.REFRESH_TOKEN, snap.refreshToken);
  }
  if (snap.roles.length) {
    safeSet(localStorage, LAST_USED_KEYS.ROLES, JSON.stringify(snap.roles));
  }
  if (snap.username) {
    safeSet(localStorage, LAST_USED_KEYS.USERNAME, snap.username);
    safeSet(localStorage, LAST_USED_KEYS.USER_NAME, snap.username);
    safeSet(localStorage, LAST_USED_KEYS.USER, snap.username);
  }
  if (snap.displayName) {
    safeSet(localStorage, LAST_USED_KEYS.DISPLAY_NAME, snap.displayName);
  }
  if (snap.tenantId != null) {
    safeSet(localStorage, LAST_USED_KEYS.TENANT_ID, String(snap.tenantId));
    safeSet(localStorage, LAST_USED_KEYS.TENANT_NAME, snap.tenantName ?? '');
    safeSet(localStorage, LAST_USED_KEYS.TENANT_CODE, snap.tenantCode ?? '');
  }
};

/**
 * Si esta pestaña no tiene token en sessionStorage, copia desde localStorage (last-used).
 * Duplicar pestaña ya copia sessionStorage; pestaña nueva vacía usa este seed.
 */
export const seedSessionFromLastUsedIfNeeded = (): TabSessionSnapshot => {
  const existing = safeGet(sessionStorage, SESSION_KEYS.TOKEN);
  if (existing) {
    return readTabSession();
  }

  const token = safeGet(localStorage, LAST_USED_KEYS.TOKEN);
  if (!token) {
    return readTabSession();
  }

  safeSet(sessionStorage, SESSION_KEYS.TOKEN, token);
  const refresh = safeGet(localStorage, LAST_USED_KEYS.REFRESH_TOKEN);
  if (refresh) safeSet(sessionStorage, SESSION_KEYS.REFRESH_TOKEN, refresh);
  const roles = safeGet(localStorage, LAST_USED_KEYS.ROLES);
  if (roles) safeSet(sessionStorage, SESSION_KEYS.ROLES, roles);
  const username =
    safeGet(localStorage, LAST_USED_KEYS.USERNAME) ||
    safeGet(localStorage, LAST_USED_KEYS.USER_NAME) ||
    safeGet(localStorage, LAST_USED_KEYS.USER);
  if (username) safeSet(sessionStorage, SESSION_KEYS.USERNAME, username);
  const displayName = safeGet(localStorage, LAST_USED_KEYS.DISPLAY_NAME);
  if (displayName) safeSet(sessionStorage, SESSION_KEYS.DISPLAY_NAME, displayName);
  const tid = safeGet(localStorage, LAST_USED_KEYS.TENANT_ID);
  if (tid) {
    safeSet(sessionStorage, SESSION_KEYS.TENANT_ID, tid);
    safeSet(sessionStorage, SESSION_KEYS.TENANT_NAME, safeGet(localStorage, LAST_USED_KEYS.TENANT_NAME) ?? '');
    safeSet(sessionStorage, SESSION_KEYS.TENANT_CODE, safeGet(localStorage, LAST_USED_KEYS.TENANT_CODE) ?? '');
  }

  return readTabSession();
};

/** Limpia sesión de esta pestaña. */
export const clearTabSession = (): void => {
  Object.values(SESSION_KEYS).forEach((key) => safeRemove(sessionStorage, key));
};

/** Limpia last-used global (tokens + tenant). No borra lista de tenants. */
export const clearLastUsedAuth = (): void => {
  safeRemove(localStorage, LAST_USED_KEYS.TOKEN);
  safeRemove(localStorage, LAST_USED_KEYS.REFRESH_TOKEN);
  safeRemove(localStorage, LAST_USED_KEYS.TENANT_ID);
  safeRemove(localStorage, LAST_USED_KEYS.TENANT_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.TENANT_CODE);
  safeRemove(localStorage, LAST_USED_KEYS.ROLES);
  safeRemove(localStorage, LAST_USED_KEYS.USERNAME);
  safeRemove(localStorage, LAST_USED_KEYS.USER_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.USER);
  safeRemove(localStorage, LAST_USED_KEYS.DISPLAY_NAME);
};

export const clearAllAuthStorage = (): void => {
  clearTabSession();
  clearLastUsedAuth();
  safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
};

let authChannel: BroadcastChannel | null = null;

export const getAuthBroadcastChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!authChannel) {
    try {
      authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    } catch {
      authChannel = null;
    }
  }
  return authChannel;
};

export const broadcastAuthMessage = (message: AuthBroadcastMessage): void => {
  try {
    getAuthBroadcastChannel()?.postMessage(message);
  } catch {
    // ignore
  }
};

/** Lee y quita ?tabTenant= de la URL. */
export const consumeTabTenantQuery = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(TAB_TENANT_QUERY);
    if (!raw) return null;
    const id = parseInt(raw, 10);
    url.searchParams.delete(TAB_TENANT_QUERY);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

export const buildOpenTenantUrl = (tenantId: number, path?: string): string => {
  const base = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const url = new URL(base, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  url.searchParams.set(TAB_TENANT_QUERY, String(tenantId));
  return url.pathname + url.search + url.hash;
};
